import React, { useState } from "react";
import { FolderHeart, Maximize2, MessageCircle, Link2, Mail, Megaphone, Video, Layout, Plus } from "lucide-react";
import { 
  SiFacebook, SiInstagram, SiTiktok, SiYoutube 
} from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";
import { Globe, AtSign } from "lucide-react";

// Type icons mapping
const typeIcons = {
  ad: Megaphone,
  landing_page: Layout,
  ugc: Video,
  email: Mail,
};

const typeColors = {
  ad: "text-blue-400",
  landing_page: "text-emerald-400",
  ugc: "text-purple-400",
  email: "text-amber-400",
};

// Platform icons mapping
const platformIcons = {
  Facebook: SiFacebook,
  Instagram: SiInstagram,
  TikTok: SiTiktok,
  LinkedIn: FaLinkedin,
  YouTube: SiYoutube,
  Email: AtSign,
  Web: Globe,
  Other: Globe,
};

const platformColors = {
  Facebook: "text-blue-500",
  Instagram: "text-pink-500",
  TikTok: "text-white",
  LinkedIn: "text-blue-400",
  YouTube: "text-red-500",
  Email: "text-amber-400",
  Web: "text-gray-400",
  Other: "text-gray-400",
};

const ContentCard = ({ item, onView, onComments, onBoard, onCopyLink, categoryColor }) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    onCopyLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const TypeIcon = typeIcons[item.content_type] || Megaphone;
  
  // Get platforms array
  const getPlatforms = () => {
    if (item.platforms && Array.isArray(item.platforms) && item.platforms.length > 0) {
      return item.platforms;
    }
    if (item.platform) {
      return item.platform.split(", ").filter(Boolean);
    }
    return [];
  };
  
  const platformsList = getPlatforms();
  const firstPlatform = platformsList[0];
  const PlatformIcon = firstPlatform ? (platformIcons[firstPlatform] || Globe) : null;

  // Determine aspect ratio based on content type
  const getAspectRatio = () => {
    if (item.content_type === "ugc") return "aspect-[9/16]";
    if (item.content_type === "ad") return "aspect-square";
    if (item.content_type === "landing_page") return "aspect-[4/3]";
    if (item.content_type === "email") return "aspect-[3/4]";
    return "aspect-[4/3]";
  };

  return (
    <div 
      className="relative rounded-2xl border border-white/[0.06] bg-[#151515] overflow-hidden transition-all duration-300 ease-out cursor-pointer group hover:border-white/[0.15] hover:shadow-[0_0_30px_rgba(168,85,247,0.08)] hover:scale-[1.02]"
      style={{ 
        boxShadow: isHovered ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)' : 'inset 0 1px 0 rgba(255,255,255,0.03)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onView()}
      data-testid={`content-card-${item.id}`}
    >
      {/* Category color strip */}
      {categoryColor && (
        <div className="absolute top-0 left-0 right-0 h-[3px] z-20" style={{ backgroundColor: categoryColor }} />
      )}

      {/* Content Image with hover overlay */}
      <div className={`relative ${getAspectRatio()} overflow-hidden bg-[#0c0c0e]`}>
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f]">
            <TypeIcon size={40} className="text-white/[0.08]" />
          </div>
        )}

        {/* Hover overlay with gradient */}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          {/* Top icons */}
          <div className="absolute top-3 right-3 flex items-center gap-1.5">
            <div 
              className={`w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm flex items-center justify-center ${typeColors[item.content_type] || "text-gray-400"} transition-transform duration-300 ${isHovered ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}
              title={item.content_type}
            >
              <TypeIcon size={14} />
            </div>
            {PlatformIcon && (
              <div 
                className={`w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm flex items-center justify-center ${platformColors[firstPlatform] || "text-gray-400"} transition-transform duration-300 delay-75 ${isHovered ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}
                title={platformsList.join(", ")}
              >
                <PlatformIcon size={14} />
              </div>
            )}
          </div>

          {/* Bottom content */}
          <div className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            <h3 className="text-[14px] text-white font-medium leading-tight line-clamp-2 mb-2">
              {item.title}
            </h3>
            
            {/* Tags preview */}
            {item.tags?.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-3">
                {item.tags.slice(0, 3).map((tag, i) => (
                  <span key={i} className="text-[10px] text-cyan-400/80 bg-cyan-400/10 rounded-full px-2 py-0.5">#{tag}</span>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onView(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-[11px] font-medium transition-all hover:bg-white/90"
              >
                <Maximize2 size={12} />
                Ouvrir
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onBoard(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm text-white text-[11px] font-medium transition-all hover:bg-white/20 border border-white/10"
              >
                <Plus size={12} />
                Board
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onComments(); }}
                className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all border border-white/10"
                title="Commentaires"
              >
                <MessageCircle size={14} />
              </button>
              <button
                onClick={handleCopy}
                className={`w-8 h-8 rounded-lg backdrop-blur-sm flex items-center justify-center transition-all border ${
                  copied
                    ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400"
                    : "bg-white/10 border-white/10 text-white/70 hover:text-white hover:bg-white/20"
                }`}
                title={copied ? "Copié !" : "Copier le lien"}
              >
                <Link2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentCard;
